/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Divider, Input, Popconfirm, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import LoadableContainer from '@components/LoadableContainer';
import {
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { StudioPhoto } from '@core/models';
import { useAppDispatch, useAuthState } from '@core/configureStore';
import {
  deleteStudioPhotos,
  fetchGalleryImages,
  updateStudioPhoto,
} from '@core/features/studio/thunks';
import { formatRelativeUtcDate } from '@utils/date.util';
import { useHeroApiService } from '@core/hooks';
import AnimatedComponent from '@components/AnimatedComponent';
import RenderStudioPhotoStatus from './RenderStudioPhotoStatus';

type Props = {
  mediaId: string;
  onClose: () => void;
};

interface DescriptionItemProps {
  title: string;
  content: React.ReactNode;
}

const DescriptionItem = ({ title, content }: DescriptionItemProps) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
    <p style={{ fontWeight: 'bold', marginRight: 10 }}>{title}:</p>
    {content}
  </div>
);

const StudioImageItemDetail = ({ mediaId }: Props) => {
  const dispatch = useAppDispatch();
  const heroApiService = useHeroApiService();
  const { user } = useAuthState();
  const [studioPhoto, setStudioPhoto] = useState<StudioPhoto | null>(null);
  const [description, setDescription] = useState<string>(studioPhoto?.description || '');
  const [tags, setTags] = useState<string[]>(studioPhoto?.labels || []);
  const [tagValue, setTagValue] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleAddTag = () => {
    if (!tags.some(tag => tag === tagValue)) {
      setTags(tags.concat([tagValue]));
    }
  };

  const handleRemoveTag = (removedTag: string) => {
    if (tags.some(tag => tag === removedTag)) {
      setTags(tags.filter(label => label !== removedTag));
    }
  };

  const handleUpdateStudioPhoto = async () => {
    if (!user || !studioPhoto) return;
    const locationId = user.selected_location.location_id;
    await dispatch(
      updateStudioPhoto({
        locationId,
        mediaId: studioPhoto.media_id,
        labels: tags,
        description,
      })
    );
  };

  const handleRemoveItem = async () => {
    if (!user || !studioPhoto) return;
    const locationId = user.selected_location.location_id;
    await dispatch(deleteStudioPhotos({ locationId, studioPhotos: [studioPhoto] }));
    await dispatch(
      fetchGalleryImages({
        locationId,
      })
    );
  };

  const itemUpdated = useMemo(
    () =>
      studioPhoto?.description !== description ||
      studioPhoto?.labels.length !== tags.length ||
      studioPhoto?.labels.some(label => tags.some(tag => tag !== label)),
    [studioPhoto, description, tags]
  );

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const locationId = user.selected_location.location_id;
        const _studioPhoto = await heroApiService.getStudioPhotoByMediaId(locationId, mediaId);
        setStudioPhoto(_studioPhoto);
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    };
    init();
  }, [mediaId, user]);

  useEffect(() => {
    setDescription(studioPhoto?.description || '');
    setTags(studioPhoto?.labels || []);
  }, [studioPhoto]);

  return (
    <AnimatedComponent.OpacityFadeInDiv delay={300}>
      <LoadableContainer isLoading={loading} loadComponent={<LoadingOutlined />}>
        <React.Fragment>
          <div>
            <div
              style={{
                backgroundImage: `url("${studioPhoto?.url}")`,
                height: 200,
                width: '100%',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                padding: '10px 20px',
              }}></div>
            {itemUpdated && (
              <Button
                type="primary"
                style={{ width: '100%', marginTop: 20 }}
                onClick={handleUpdateStudioPhoto}>
                <SaveOutlined />
                Update
              </Button>
            )}
            <Divider />
            <LoadableContainer loadComponent={<></>} isLoading={!studioPhoto}>
              <React.Fragment>
                <DescriptionItem
                  title="Status"
                  content={<RenderStudioPhotoStatus studioPhoto={studioPhoto as any} />}
                />
                <DescriptionItem
                  title="Uploaded at"
                  content={<div>{formatRelativeUtcDate(studioPhoto?.updated_date as any)}</div>}
                />
                <DescriptionItem title="Description" content="" />
                <Input.TextArea
                  value={description || ''}
                  placeholder="Enter description"
                  onChange={e => setDescription(e.target.value)}
                />
                <Popconfirm
                  onConfirm={handleRemoveItem}
                  title="Delete the image"
                  description="Are you sure to delete this image?"
                  okButtonProps={{
                    size: 'middle',
                  }}
                  cancelButtonProps={{
                    size: 'middle',
                  }}
                  okText="Yes"
                  cancelText="No">
                  <Button danger style={{ width: '100%', marginTop: 20 }}>
                    <DeleteOutlined /> Delete
                  </Button>
                </Popconfirm>
              </React.Fragment>
            </LoadableContainer>
            <Divider />
            <div style={{ display: 'flex' }}>
              <Input
                placeholder="# Enter Tag"
                value={tagValue}
                onChange={e => setTagValue(e.target.value)}></Input>
              <Button
                disabled={tags.includes(tagValue)}
                style={{ marginLeft: 10 }}
                onClick={handleAddTag}>
                <PlusOutlined /> Add
              </Button>
            </div>
            <LoadableContainer
              isLoading={tags.length === 0}
              loadComponent={<div>No tag found</div>}>
              <div style={{ display: 'flex', flexWrap: 'wrap', width: '400px', marginTop: 20 }}>
                {tags.map(tag => (
                  <Tag color="blue" style={{ marginTop: '10px' }}>
                    {tag}{' '}
                    <CloseOutlined
                      onClick={() => handleRemoveTag(tag)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Tag>
                ))}
              </div>
            </LoadableContainer>
          </div>
        </React.Fragment>
      </LoadableContainer>
    </AnimatedComponent.OpacityFadeInDiv>
  );
};

export default StudioImageItemDetail;
